import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { rewardService } from '../services/reward.service';
import { supabaseRewardService } from '../services/supabase/reward.service';
import { Reward, EarnedReward } from '../models';
import type { MilestoneReward } from '@/types/rewards';
import { useAuthStore } from './auth';
import { useChildrenStore } from './children';
import { useRealtimeSync } from '@/composables/useRealtimeSync';
import { useAppContextStore } from './appContext';

export const useRewardsStore = defineStore('rewards', () => {
  const rewards = ref<Reward[]>([]);
  const earnedRewards = ref<EarnedReward[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Cached per-child rewards (stale-while-revalidate). Keyed by householdId.
  const childRewardsByHousehold = ref<Map<string, { map: Map<string, MilestoneReward[]>; ts: number }>>(new Map());
  const REWARDS_SWR_TTL = 5 * 60 * 1000; // 5 min: fresh window; older → refresh in bg but still serve

  // Cached household collected rewards ("à remettre"), stale-while-revalidate, keyed by householdId.
  // This is the costly join (collected_rewards → children → rewards). Cache + dedupe collapses it to one query.
  type CollectedRows = Awaited<ReturnType<typeof supabaseRewardService.getHouseholdCollectedRewards>>;
  const collectedRewardsByHousehold = ref<Map<string, { rows: CollectedRows; ts: number }>>(new Map());
  // In-flight dedupe: if a refresh is already running for a household, concurrent callers share the
  // same promise instead of firing a second identical join (kills the cold-start 2× — same idea as
  // the avatars store's pendingAvatarLoads map).
  const pendingCollected = new Map<string, Promise<CollectedRows>>();

  const authStore = useAuthStore();
  const childrenStore = useChildrenStore();
  const realtimeSync = useRealtimeSync();
  let currentHouseholdId: string | null = null;

  // Actions
  async function loadRewards() {
    if (!authStore.user) return;

    loading.value = true;
    error.value = null;

    try {
      rewards.value = await rewardService.getRewardsByUserId(authStore.user.id);
    } catch (err: any) {
      console.error('Error loading rewards:', err);
      error.value = err.message || 'Failed to load rewards';
    } finally {
      loading.value = false;
    }
  }

  async function loadEarnedRewardsForChild(childId: string) {
    loading.value = true;
    error.value = null;

    try {
      earnedRewards.value = await rewardService.getEarnedRewardsByChild(childId);
    } catch (err: any) {
      console.error('Error loading earned rewards:', err);
      error.value = err.message || 'Failed to load earned rewards';
    } finally {
      loading.value = false;
    }
  }

  // Stale-while-revalidate per-child rewards for a household. Serves cached data
  // immediately when present (refreshing silently in the background unless still
  // fresh), otherwise fetches the batch (2 queries replacing N RPCs) and caches it.
  async function loadChildRewards(
    householdId: string,
    childIds: string[],
    opts?: { force?: boolean },
  ): Promise<Map<string, MilestoneReward[]>> {
    if (!householdId) return new Map();
    const cached = childRewardsByHousehold.value.get(householdId);
    const fresh = !!cached && Date.now() - cached.ts < REWARDS_SWR_TTL;
    if (cached && !opts?.force) {
      // Serve cache immediately; refresh in background only when stale.
      if (!fresh) void refreshChildRewards(householdId, childIds);
      return cached.map;
    }
    return await refreshChildRewards(householdId, childIds);
  }

  async function refreshChildRewards(
    householdId: string,
    childIds: string[],
  ): Promise<Map<string, MilestoneReward[]>> {
    const map = (await supabaseRewardService.getRewardsForChildren(
      childIds,
      householdId,
    )) as Map<string, MilestoneReward[]>;
    childRewardsByHousehold.value.set(householdId, { map, ts: Date.now() });
    return map;
  }

  /**
   * La photo d'une récompense (#354), pour les écrans qui n'ont qu'une
   * récompense obtenue en main (rangée « À remettre », chemin) : les RPC des
   * récompenses obtenues ne la portent pas. On la cherche dans ce que le store
   * a déjà chargé, la liste tenue par le temps réel d'abord.
   */
  function photoOf(rewardId: string | null | undefined): string | null {
    if (!rewardId) return null;
    const live = rewards.value.find(r => r.id === rewardId);
    if (live && 'photo' in live) return live.photo ?? null;
    for (const { map } of childRewardsByHousehold.value.values()) {
      for (const list of map.values()) {
        const hit = list.find(r => r.id === rewardId);
        if (hit) return (hit as { photo?: string | null }).photo ?? null;
      }
    }
    return null;
  }

  /** True when we already have cached child-rewards for the household (drives silent refresh). */
  function hasChildRewardsCache(householdId: string): boolean {
    return childRewardsByHousehold.value.has(householdId);
  }

  // Stale-while-revalidate household collected rewards. Serves cached rows immediately when present
  // (refreshing silently in the background only when stale), otherwise fetches once. Concurrent cold
  // callers are de-duped via pendingCollected so the costly join runs a single time.
  async function loadHouseholdCollectedRewards(
    householdId: string,
    opts?: { force?: boolean },
  ): Promise<CollectedRows> {
    if (!householdId) return [];
    const cached = collectedRewardsByHousehold.value.get(householdId);
    const fresh = !!cached && Date.now() - cached.ts < REWARDS_SWR_TTL;
    if (cached && !opts?.force) {
      if (!fresh) void refreshHouseholdCollectedRewards(householdId); // stale → silent bg refresh
      return cached.rows;
    }
    return await refreshHouseholdCollectedRewards(householdId);
  }

  async function refreshHouseholdCollectedRewards(householdId: string): Promise<CollectedRows> {
    // Share an in-flight refresh so two callers in the same tick fire the join only once.
    const inFlight = pendingCollected.get(householdId);
    if (inFlight) return inFlight;

    const promise = (async () => {
      const rows = await supabaseRewardService.getHouseholdCollectedRewards(householdId);
      collectedRewardsByHousehold.value.set(householdId, { rows, ts: Date.now() });
      return rows;
    })();

    pendingCollected.set(householdId, promise);
    try {
      return await promise;
    } finally {
      if (pendingCollected.get(householdId) === promise) {
        pendingCollected.delete(householdId);
      }
    }
  }

  /** True when we already have cached collected rewards for the household (drives the spinner gate). */
  function hasCollectedRewardsCache(householdId: string): boolean {
    return collectedRewardsByHousehold.value.has(householdId);
  }

  /** Force a fresh fetch of the household collected rewards (call after a review/claim mutation). */
  async function invalidateCollectedRewards(householdId: string): Promise<CollectedRows> {
    return await loadHouseholdCollectedRewards(householdId, { force: true });
  }

  async function createReward(rewardData: Omit<Reward, 'id'>) {
    if (!authStore.user) throw new Error('User not authenticated');

    loading.value = true;
    error.value = null;

    try {
      const newReward = await rewardService.createReward({
        ...rewardData,
        user_id: authStore.user.id
      });

      rewards.value.push(newReward);

      // reward_created est émis par rewardService.createReward, commun à ce
      // store et au formulaire (qui passe par addMilestoneReward).

      return newReward;
    } catch (err: any) {
      console.error('Error creating reward:', err);
      error.value = err.message || 'Failed to create reward';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateReward(rewardId: string, rewardData: Partial<Reward>) {
    loading.value = true;
    error.value = null;

    try {
      await rewardService.updateReward(rewardId, rewardData);

      const index = rewards.value.findIndex(r => r.id === rewardId);
      if (index !== -1) {
        rewards.value[index] = { ...rewards.value[index], ...rewardData };
      }

      return rewards.value[index];
    } catch (err: any) {
      console.error('Error updating reward:', err);
      error.value = err.message || 'Failed to update reward';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteReward(rewardId: string) {
    loading.value = true;
    error.value = null;

    try {
      await rewardService.deleteReward(rewardId);
      rewards.value = rewards.value.filter(r => r.id !== rewardId);
      
      // Track reward deletion
    } catch (err: any) {
      console.error('Error deleting reward:', err);
      error.value = err.message || 'Failed to delete reward';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function checkForEarnedRewards() {
    if (!authStore.user || !childrenStore.selectedChild) return [];

    loading.value = true;
    error.value = null;

    try {
      const newRewards = await rewardService.checkForEarnedRewards(
        childrenStore.selectedChild.id,
        authStore.user.id
      );

      if (newRewards.length > 0) {
        earnedRewards.value = [...newRewards, ...earnedRewards.value];
        // reward_unlocked retiré : reward_triggered mesure le même fait depuis
        // la source serveur, sur le chemin réellement emprunté.
      }

      return newRewards;
    } catch (err: any) {
      console.error('Error checking for earned rewards:', err);
      error.value = err.message || 'Failed to check for earned rewards';
      return [];
    } finally {
      loading.value = false;
    }
  }

  async function claimReward(earnedRewardId: string) {
    loading.value = true;
    error.value = null;

    try {
      
      // Utiliser la version implémentée de claimReward qui analyse le chemin du document
      // pour extraire l'ID de l'enfant et appeler claimCollectedReward
      await rewardService.claimReward(earnedRewardId);

      // Mettre à jour la liste des récompenses gagnées
      const index = earnedRewards.value.findIndex(r => r.id === earnedRewardId);
      if (index !== -1) {
        earnedRewards.value[index] = {
          ...earnedRewards.value[index],
          is_claimed: true,
          claimed_at: new Date().toISOString()
        };
      }

      return earnedRewards.value[index];
    } catch (err: any) {
      console.error('Error claiming reward:', err);
      error.value = err.message || 'Failed to claim reward';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function getAvailableRewardsForChild(childId: string) {
    if (!authStore.user) return [];

    loading.value = true;
    error.value = null;

    try {
      return await rewardService.getAvailableRewardsForChild(childId, authStore.user.id);
    } catch (err: any) {
      console.error('Error getting available rewards:', err);
      error.value = err.message || 'Failed to get available rewards';
      return [];
    } finally {
      loading.value = false;
    }
  }

  async function loadCollectiveRewards() {
    if (!authStore.user) return;

    loading.value = true;
    error.value = null;

    try {
      const collectiveRewards = await rewardService.getAvailableCollectiveRewards(authStore.user.id);
      rewards.value = [...rewards.value.filter(r => !r.is_collective), ...collectiveRewards];
    } catch (err: any) {
      console.error('Error loading collective rewards:', err);
      error.value = err.message || 'Failed to load collective rewards';
    } finally {
      loading.value = false;
    }
  }

  async function checkForCollectiveRewards() {
    if (!authStore.user) return;

    loading.value = true;
    error.value = null;

    try {
      const newCollectiveRewards = await rewardService.checkForCollectiveRewards(authStore.user.id);
      earnedRewards.value = [...earnedRewards.value, ...newCollectiveRewards];
    } catch (err: any) {
      console.error('Error checking for collective rewards:', err);
      error.value = err.message || 'Failed to check for collective rewards';
    } finally {
      loading.value = false;
    }
  }

  // Realtime synchronization
  function startRealtimeSync(householdId: string) {
    // Éviter de créer plusieurs subscriptions pour le même household
    if (currentHouseholdId === householdId) {
      console.log('[Rewards Store] Already syncing this household, skipping')
      return
    }

    // Arrêter les anciennes subscriptions si on change de household
    if (currentHouseholdId) {
      stopRealtimeSync()
    }

    currentHouseholdId = householdId
    console.log('[Rewards Store] Starting realtime sync for household:', householdId)

    // Synchroniser la table rewards
    realtimeSync.subscribeToTable<Reward>(
      `rewards-${householdId}`,
      'rewards',
      {
        onInsert: (payload) => {
          const newReward = payload.new as Reward
          // Éviter les doublons
          if (!rewards.value.find(r => r.id === newReward.id)) {
            rewards.value.unshift(newReward)
            console.log('[Rewards Store] Reward inserted via realtime:', newReward.title)
          }
        },
        onUpdate: (payload) => {
          const updatedReward = payload.new as Reward
          const index = rewards.value.findIndex(r => r.id === updatedReward.id)
          if (index !== -1) {
            rewards.value[index] = updatedReward
            console.log('[Rewards Store] Reward updated via realtime:', updatedReward.title)
          }
        },
        onDelete: (payload) => {
          const deletedReward = payload.old as Reward
          rewards.value = rewards.value.filter(r => r.id !== deletedReward.id)
          console.log('[Rewards Store] Reward deleted via realtime:', deletedReward.id)
        }
      },
      `household_id=eq.${householdId}`
    )

    // Synchroniser la table collected_rewards
    realtimeSync.subscribeToTable<EarnedReward>(
      `collected-rewards-${householdId}`,
      'collected_rewards',
      {
        onInsert: (payload) => {
          const newEarnedReward = payload.new as EarnedReward
          // Éviter les doublons
          if (!earnedRewards.value.find(er => er.id === newEarnedReward.id)) {
            earnedRewards.value.unshift(newEarnedReward)
            console.log('[Rewards Store] Earned reward inserted via realtime')
          }
          // The collected_rewards rows changed externally → drop the SWR cache so the
          // next read refetches fresh (don't serve pre-change rows).
          collectedRewardsByHousehold.value.delete(householdId)
        },
        onUpdate: (payload) => {
          const updatedEarnedReward = payload.new as EarnedReward
          const index = earnedRewards.value.findIndex(er => er.id === updatedEarnedReward.id)
          if (index !== -1) {
            earnedRewards.value[index] = updatedEarnedReward
            console.log('[Rewards Store] Earned reward updated via realtime')
          }
          collectedRewardsByHousehold.value.delete(householdId)
        },
        onDelete: (payload) => {
          const deletedEarnedReward = payload.old as EarnedReward
          earnedRewards.value = earnedRewards.value.filter(er => er.id !== deletedEarnedReward.id)
          console.log('[Rewards Store] Earned reward deleted via realtime')
          collectedRewardsByHousehold.value.delete(householdId)
        }
      }
    )
  }

  function stopRealtimeSync() {
    if (currentHouseholdId === null) return
    console.log('[Rewards Store] Stopping realtime sync')
    realtimeSync.unsubscribeAll()
    currentHouseholdId = null
  }

  // Getters
  const allRewards = computed(() => rewards.value);
  const allEarnedRewards = computed(() => earnedRewards.value);
  const unclaimedRewards = computed(() =>
    earnedRewards.value.filter(reward => !reward.is_claimed)
  );
  const collectiveRewards = computed(() =>
    rewards.value.filter(reward => reward.is_collective)
  );
  const availableCollectiveRewards = computed(() =>
    earnedRewards.value.filter(reward => reward.is_collective && !reward.is_claimed)
  );

  // Auto-start realtime sync when household is loaded
  // MAIS ne pas démarrer si on est sur la page d'onboarding (éviter les boucles)
  const appContextStore = useAppContextStore();
  watch(() => appContextStore.currentHouseholdId, (householdId) => {
    if (householdId) {
      // Ne pas démarrer la sync realtime si on est sur la page d'onboarding
      if (typeof window !== 'undefined' && window.location.pathname === '/onboarding') {
        console.log('[Rewards Store] On onboarding page, skipping realtime sync to avoid loops');
        return;
      }
      startRealtimeSync(householdId);
    } else {
      stopRealtimeSync();
    }
  }, { immediate: true });

  return {
    // State
    rewards,
    earnedRewards,
    loading,
    error,

    // Actions
    loadRewards,
    loadEarnedRewardsForChild,
    loadChildRewards,
    photoOf,
    hasChildRewardsCache,
    loadHouseholdCollectedRewards,
    refreshHouseholdCollectedRewards,
    hasCollectedRewardsCache,
    invalidateCollectedRewards,
    createReward,
    updateReward,
    deleteReward,
    checkForEarnedRewards,
    claimReward,
    getAvailableRewardsForChild,
    loadCollectiveRewards,
    checkForCollectiveRewards,

    // Realtime sync
    startRealtimeSync,
    stopRealtimeSync,

    // Getters
    allRewards,
    allEarnedRewards,
    unclaimedRewards,
    collectiveRewards,
    availableCollectiveRewards
  };
});
